import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Upload, Paperclip, AlertCircle, Loader2, Search, BookOpen } from 'lucide-react';
import { sourcesApi } from '@/lib/api/sourcesApi';
import {
    Source,
    ModuleRef,
    SOURCE_ALLOWED_EXTENSIONS,
    SOURCE_MAX_FILE_BYTES,
} from '@/lib/models/source';
import { SourceList, SourceViewerModal } from '@/components/sources';
import { ActionButton, OutlineButton } from '@/components/custom-ui/action-button';
import { LoadingState } from '@/components/custom-ui/empty-state';
import { NEXILINK_CARD_WHITE_ELEVATED } from '@/lib/nexilink-card-styles';
import { ExplainerCard } from '@/components/custom-ui/ExplainerCard';
import { Lightbulb } from 'lucide-react';

// SourceLibraryPage — top-level dashboard surface for managing the
// employer's source library. Counterpart to the compact rail in V2
// chat: same data, same viewer modal, but with upload + delete
// actions and the wider table layout.
//
// Phase 3 of the source-library redesign (see docs/source-library-redesign.md).

const SourceLibraryPage: React.FC = () => {
    const [sources, setSources] = useState<Source[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [viewerSource, setViewerSource] = useState<Source | null>(null);
    const [pendingDelete, setPendingDelete] = useState<Source | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    // Search filter — passed straight to SourceList full variant,
    // which applies the same case-insensitive substring filter the
    // compact rail uses (single source of truth: useGroupedSources).
    const [search, setSearch] = useState('');

    // Drag-drop visual state — same pattern as LinkAgentModal.
    const [isDraggingFile, setIsDraggingFile] = useState(false);
    const dragDepthRef = useRef(0);

    const loadSources = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const list = await sourcesApi.listSources();
            setSources(list);
        } catch (err: any) {
            setError(err?.message || 'Kunde inte ladda biblioteket.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSources();
    }, [loadSources]);

    // ── Upload ────────────────────────────────────────────────────

    const validateFile = (file: File): string | null => {
        if (file.size > SOURCE_MAX_FILE_BYTES) {
            return `Filen är för stor (max ${Math.round(SOURCE_MAX_FILE_BYTES / 1024 / 1024)} MB).`;
        }
        const lowerName = file.name.toLowerCase();
        const hasAllowedExt = SOURCE_ALLOWED_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
        if (!hasAllowedExt) {
            return `Filtypen stöds inte. Tillåtna filändelser: PDF, bilder, text/markdown, kod.`;
        }
        return null;
    };

    const handleUpload = async (files: File[]) => {
        if (files.length === 0) return;
        setUploadError(null);
        setUploading(true);
        try {
            for (const file of files) {
                const err = validateFile(file);
                if (err) {
                    setUploadError(`${file.name}: ${err}`);
                    continue;
                }
                await sourcesApi.uploadSource(file);
            }
            await loadSources();
        } catch (err: any) {
            setUploadError(err?.message || 'Uppladdningen misslyckades.');
        } finally {
            setUploading(false);
        }
    };

    const onPickFile = () => fileInputRef.current?.click();
    const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        // Reset input so picking the same file twice in a row works.
        e.target.value = '';
        handleUpload(files);
    };

    // Drag handlers — dragenter/leave fire per descendant element,
    // so a depth counter avoids flicker on the overlay.
    const onDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        if (!e.dataTransfer?.types?.includes('Files')) return;
        dragDepthRef.current += 1;
        setIsDraggingFile(true);
    };
    const onDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) setIsDraggingFile(false);
    };
    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
    };
    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        dragDepthRef.current = 0;
        setIsDraggingFile(false);
        const files = Array.from(e.dataTransfer.files || []);
        handleUpload(files);
    };

    // ── Delete ────────────────────────────────────────────────────

    const confirmDelete = async () => {
        if (!pendingDelete) return;
        setDeleteError(null);
        setDeleting(true);
        try {
            await sourcesApi.deleteSource(pendingDelete.id);
            setPendingDelete(null);
            await loadSources();
        } catch (err: any) {
            // Backend rejects with 400 when a module still references
            // the source — surface that message rather than a generic
            // failure so the user knows to detach it first.
            setDeleteError(err?.message || 'Kunde inte radera källan.');
        } finally {
            setDeleting(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────

    return (
        <div
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDragOver={onDragOver}
            onDrop={onDrop}
            className="relative space-y-4"
        >
            {isDraggingFile && (
                <div className="absolute inset-0 z-10 bg-[#1A4D4F]/10 backdrop-blur-sm border-4 border-dashed border-[#1A4D4F] rounded-2xl flex items-center justify-center pointer-events-none">
                    <div className="bg-white px-6 py-4 rounded-xl shadow-lg flex items-center gap-3">
                        <Paperclip className="w-6 h-6 text-[#1A4D4F]" />
                        <span className="text-[#1A4D4F] font-medium">
                            Släpp filerna här för att lägga till dem i biblioteket
                        </span>
                    </div>
                </div>
            )}

            <ExplainerCard id="employer-library" icon={Lightbulb} tone="amber" title="Så funkar biblioteket">
                <p className="text-sm text-gray-600 mt-0.5">
                    Ladda upp dokument och <strong>källor</strong> — Link använder dem som underlag när den hjälper dig skapa annonser och moduler. Dra in filer var som helst på sidan för att lägga till dem.
                </p>
            </ExplainerCard>

            {/* Toolbar — title lives in the top header strip (TopHeader). Search
                (left) + upload (right) read as one row, matching other pages. */}
            <div className="flex items-center justify-between gap-4">
                <div className="relative w-full max-w-sm">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Sök i biblioteket…"
                        disabled={sources.length === 0}
                        className="w-full pl-9 pr-3 py-2.5 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-shadow"
                    />
                </div>
                <ActionButton
                    icon={uploading ? Loader2 : Upload}
                    onClick={onPickFile}
                    disabled={uploading}
                    className={`flex-shrink-0 ${uploading ? '[&_svg]:animate-spin' : ''}`}
                >
                    {uploading ? 'Laddar upp…' : 'Ladda upp'}
                </ActionButton>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={SOURCE_ALLOWED_EXTENSIONS.join(',')}
                    onChange={onFileInputChange}
                    className="hidden"
                />
            </div>

            {uploadError && (
                <div className="mb-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-red-700">{uploadError}</span>
                </div>
            )}

            {/* List */}
            <div className={`${NEXILINK_CARD_WHITE_ELEVATED} overflow-hidden`}>
                {loading ? (
                    <LoadingState message="Laddar biblioteket…" />
                ) : error ? (
                    <div className="p-8 text-center text-sm text-red-600">{error}</div>
                ) : (
                    <SourceList
                        variant="full"
                        sources={sources}
                        search={search}
                        onSourceClick={setViewerSource}
                        onDelete={setPendingDelete}
                    />
                )}
            </div>

            {viewerSource && (
                <SourceViewerModal
                    source={viewerSource}
                    onClose={() => setViewerSource(null)}
                />
            )}

            {pendingDelete && (
                <DeleteConfirmDialog
                    source={pendingDelete}
                    deleting={deleting}
                    error={deleteError}
                    onConfirm={confirmDelete}
                    onCancel={() => {
                        if (deleting) return;
                        setPendingDelete(null);
                        setDeleteError(null);
                    }}
                />
            )}
        </div>
    );
};

// Inline delete-confirmation dialog. Surfaces the backend's "module
// still references this source" message inline so the user can act
// on it (detach from the module first, then try again).
const DeleteConfirmDialog: React.FC<{
    source: Source;
    deleting: boolean;
    error: string | null;
    onConfirm: () => void;
    onCancel: () => void;
}> = ({ source, deleting, error, onConfirm, onCancel }) => {
    // Pre-fetch the list of modules referencing this source so the
    // dialog can show "used in: A, B, C" before the user even tries
    // to delete. Skipped when used_in_count === 0 (delete will succeed).
    // Failures fall back to null and the dialog reads as if the count
    // were unknown — the backend will still reject and surface the
    // error inline.
    const [usedIn, setUsedIn] = useState<ModuleRef[] | null>(null);
    useEffect(() => {
        if (source.used_in_count <= 0) {
            setUsedIn([]);
            return;
        }
        let cancelled = false;
        sourcesApi
            .listModulesUsingSource(source.id)
            .then((refs) => {
                if (!cancelled) setUsedIn(refs);
            })
            .catch(() => {
                if (!cancelled) setUsedIn(null);
            });
        return () => {
            cancelled = true;
        };
    }, [source.id, source.used_in_count]);

    const blocked = source.used_in_count > 0;

    return (
        <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
            onClick={onCancel}
        >
            <div
                className="bg-white rounded-2xl w-full max-w-md p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <h3 className="text-lg font-bold text-gray-900 mb-2">Radera källan?</h3>
                <p className="text-sm text-gray-700">
                    <strong className="font-medium">{source.original_name}</strong> tas bort från
                    biblioteket. Det går inte att ångra.
                </p>

                {blocked && (
                    <div className="mt-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <BookOpen className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-amber-900">
                            <div className="font-medium mb-1">
                                Den här källan används i {source.used_in_count}{' '}
                                {source.used_in_count === 1 ? 'modul' : 'moduler'}.
                            </div>
                            <div className="text-amber-800">
                                Koppla bort den från {source.used_in_count === 1 ? 'modulen' : 'modulerna'}{' '}
                                först — annars nekas raderingen.
                            </div>
                            {usedIn && usedIn.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                    {usedIn.map((m) => (
                                        <RouterLink
                                            key={m.id}
                                            to={`/dashboard/onboarding/modules/${m.id}`}
                                            onClick={onCancel}
                                            className="inline-flex items-center px-2 py-0.5 text-xs bg-white border border-amber-300 hover:border-amber-500 hover:bg-amber-100 text-amber-900 rounded transition-colors"
                                        >
                                            {m.module_name}
                                        </RouterLink>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {error && (
                    <div className="mt-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-red-700">{error}</span>
                    </div>
                )}

                <div className="mt-6 flex items-center justify-end gap-2">
                    <OutlineButton onClick={onCancel} disabled={deleting}>
                        Avbryt
                    </OutlineButton>
                    <ActionButton
                        onClick={onConfirm}
                        disabled={deleting || blocked}
                        className={
                            '!bg-red-600 hover:!bg-red-700 ' + (deleting ? '[&_svg]:animate-spin' : '')
                        }
                        icon={deleting ? Loader2 : undefined}
                    >
                        {deleting ? 'Raderar…' : 'Radera'}
                    </ActionButton>
                </div>
            </div>
        </div>
    );
};

export default SourceLibraryPage;
